import { useTranslation } from 'react-i18next';

import { errorMessageKey } from '@/shared/lib/errors';
import { Button } from '@/shared/ui/Button';
import { useSessionStore } from '@/stores/session.store';
import { useUiStore } from '@/stores/ui.store';

import { ProfileForm, type ProfileFormValues } from '../components/ProfileForm';
import { useUpsertProfile } from '../hooks/use-profile';
import { deriveLegacyRole } from '../model/profile';

/** First-run flow: pick roles + fill profile in one screen (D-014 — multi-role). */
export function OnboardingRoute(): JSX.Element {
  const { t } = useTranslation();
  const userId = useSessionStore((s) => s.userId);
  const toggleLocale = useUiStore((s) => s.toggleLocale);
  const upsert = useUpsertProfile();

  function handleSubmit(values: ProfileFormValues): void {
    if (!userId) return;
    upsert.mutate({
      id: userId,
      // `role` is derived from the chosen flags — the legacy column stays
      // populated so pre-D-014 readers still work (see D-014 + AD-032).
      role: deriveLegacyRole(values),
      onboardingComplete: true,
      ...values,
    });
  }

  return (
    <main className="page stack">
      <div className="row row--between">
        <h1>{t('onboarding.chooseRoleTitle')}</h1>
        <Button variant="text" onClick={toggleLocale}>
          {t('common.changeLanguage')}
        </Button>
      </div>
      <p className="muted">{t('onboarding.chooseRoleSubtitle')}</p>
      <ProfileForm
        submitLabel={t('onboarding.finish')}
        submitting={upsert.isPending}
        serverError={upsert.isError ? t(errorMessageKey(upsert.error)) : null}
        onSubmit={handleSubmit}
      />
    </main>
  );
}
